import React, { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  FiAlertCircle,
  FiCheck,
  FiCopy,
  FiRefreshCw,
  FiX,
} from "react-icons/fi";
import "./FinancingSimulatorPage.css";

// URL do simulador personalizado (Passo 8 do manual: "Copie seu link").
// Para trocar de simulador, basta atualizar o `pid` abaixo pelo link enviado
// pela Credimorar.
const SIMULADOR_URL =
  "https://credimorar.me/simulador/embed?pid=a1dca6f2-910d-439a-bb76-7ab8c89df2fe";

const buildIframeSnippet = (url) =>
  `<iframe id="resizeFrame" src="${url}" style="width:100%;height:99%;border:0;" scrolling="yes"></iframe>`;

// Passo a passo do "Manual de customização do simulador" (Credimorar).
const GUIDE_STEPS = [
  {
    title: "Passo 1 · Envie a imagem da sua empresa",
    body: 'No campo Logo, clique em "Upload" e selecione a imagem.',
    bullets: [
      "Formato PNG, fundo transparente e horizontal (mais larga do que alta).",
      "Arquivo com no máximo 20kb.",
      "Medida ideal: 288px de largura por 75px de altura.",
      "Evite logos brancas ou muito claras — o cabeçalho tem fundo claro.",
    ],
  },
  {
    title: "Passo 2 · Escolha as cores do cabeçalho",
    body: "Na seção Cabeçalho, defina a cor de fundo e a cor do texto da barra superior, onde também fica a sua logo. Use as cores principais da identidade visual, mantendo bom contraste entre fundo e texto.",
  },
  {
    title: "Passo 3 · Escolha as cores dos textos",
    body: "Na seção Textos, defina a cor dos textos principais (informações em destaque) e dos secundários (apoio). Use um tom de maior destaque nos principais e um mais suave nos secundários para criar hierarquia visual.",
  },
  {
    title: "Passo 4 · Personalize os botões",
    body: "Na seção Botões você ajusta de uma vez:",
    bullets: [
      "Cor de fundo e cor do texto no estado normal.",
      "Cor de fundo e cor do texto no hover (mouse por cima).",
      "Cor de fundo e cor do texto do indicador de etapa.",
      "Arredondamento das bordas, de 0 a 30 — quanto maior, mais arredondado.",
    ],
  },
  {
    title: "Passo 5 · Escolha a cor do carregamento",
    body: "Na seção Carregamento, defina a cor do ícone exibido enquanto o simulador processa as informações. Use uma cor da marca, alinhada às demais configurações.",
  },
  {
    title: "Passo 6 · Confira antes de salvar",
    body: "Navegue pelas abas da pré-visualização — Etapa 1, Etapa 2, Resultado, Solicitação Enviada, Erro e Loading — e verifique se as cores estão legíveis e combinam entre si. A pré-visualização atualiza na hora.",
  },
  {
    title: "Passo 7 · Salve ou restaure",
    body: 'Clique em "Salvar" para aplicar as mudanças. Para voltar às cores originais do sistema, clique em "Restaurar padrão".',
  },
  {
    title: "Passo 8 · Copie seu link personalizado",
    body: 'Clique em "Copie seu link" para pegar o endereço exclusivo do seu simulador já personalizado.',
  },
  {
    title: "Passo 9 · Integre no seu site",
    body: "Peça ao time de tecnologia para embutir o <iframe> copiado em um elemento HTML do site. Após a publicação, o simulador carrega já customizado.",
    note: "Lembre-se de enviar ao seu consultor um número de WhatsApp e um e-mail de contato, para que os leads gerados pelo simulador sejam direcionados para você.",
  },
];

const FinancingSimulatorPage = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [isGuideOpen, setIsGuideOpen] = useState(false);
  const [copied, setCopied] = useState("");
  const copyTimeoutRef = useRef(null);

  // Se o simulador não responder, mostramos um aviso em vez de um quadro vazio.
  useEffect(() => {
    if (!isLoading) return undefined;

    const timeoutId = setTimeout(() => {
      setIsLoading(false);
      setHasError(true);
    }, 20000);

    return () => clearTimeout(timeoutId);
  }, [isLoading, reloadKey]);

  useEffect(
    () => () => {
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
    },
    [],
  );

  const handleReload = useCallback(() => {
    setIsLoading(true);
    setHasError(false);
    setReloadKey((key) => key + 1);
  }, []);

  const handleCopy = useCallback(async (kind, value) => {
    try {
      await navigator.clipboard.writeText(value);
    } catch (error) {
      const textarea = document.createElement("textarea");
      textarea.value = value;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
    }

    setCopied(kind);
    if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
    copyTimeoutRef.current = setTimeout(() => setCopied(""), 2000);
  }, []);

  return (
    <div className="fin-page">
      <div className="fin-card">
        <div className="fin-card-accent" />
        <div className="fin-header">
          <div className="fin-header-info">
            <p className="fin-eyebrow">Operações</p>
            <h1 className="fin-title">
              Simulador de Financiamento Imobiliário
            </h1>
            <p className="fin-subtitle">
              Simule financiamentos para seus clientes sem sair da plataforma.
            </p>
          </div>
        </div>
      </div>

      <div className="fin-card fin-frame-card">
        <div className="fin-frame">
          <iframe
            key={reloadKey}
            id="resizeFrame"
            title="Simulador de Financiamento Imobiliário"
            src={SIMULADOR_URL}
            scrolling="yes"
            allow="clipboard-write"
            onLoad={() => {
              setIsLoading(false);
              setHasError(false);
            }}
          />
        </div>

        {isLoading && (
          <div className="fin-overlay">
            <span className="fin-spinner" />
            <p className="fin-overlay-text">Carregando simulador...</p>
          </div>
        )}

        {hasError && (
          <div className="fin-overlay fin-overlay--error">
            <span className="fin-error-icon">
              <FiAlertCircle size={20} />
            </span>
            <p className="fin-error-title">
              Não foi possível carregar o simulador
            </p>
            <p className="fin-error-help">
              Verifique sua conexão e tente novamente. Se o problema persistir,
              abra o simulador em uma nova aba.
            </p>
            <button
              onClick={handleReload}
              className="fin-btn fin-btn--primary fin-btn--retry"
            >
              <FiRefreshCw size={14} />
              Tentar novamente
            </button>
          </div>
        )}
      </div>

      <AnimatePresence>
        {isGuideOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="fin-guide-backdrop"
              onClick={() => setIsGuideOpen(false)}
            />
            <motion.aside
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ duration: 0.24, ease: [0.4, 0, 0.2, 1] }}
              className="fin-guide"
            >
              <div className="fin-guide-header">
                <div>
                  <p className="fin-eyebrow">Manual</p>
                  <h2 className="fin-guide-title">
                    Como customizar seu simulador
                  </h2>
                </div>
                <button
                  onClick={() => setIsGuideOpen(false)}
                  className="fin-guide-close"
                  aria-label="Fechar"
                >
                  <FiX size={18} />
                </button>
              </div>

              <div className="fin-guide-body">
                <p className="fin-guide-intro">
                  À esquerda do editor da Credimorar você personaliza a imagem
                  da sua empresa e as cores do simulador. À direita, acompanha
                  como tudo vai ficar, em abas que mostram cada etapa: Etapas 1
                  e 2, Resultado, Solicitação Enviada, Erro e Carregamento.
                </p>

                <div className="fin-guide-steps">
                  {GUIDE_STEPS.map((step) => (
                    <div key={step.title} className="fin-step">
                      <h3 className="fin-step-title">{step.title}</h3>
                      <p className="fin-step-body">{step.body}</p>
                      {step.bullets && (
                        <ul className="fin-step-list">
                          {step.bullets.map((bullet) => (
                            <li key={bullet}>
                              <span className="fin-step-bullet" />
                              {bullet}
                            </li>
                          ))}
                        </ul>
                      )}
                      {step.note && (
                        <p className="fin-step-note">
                          <strong>Observação:</strong> {step.note}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="fin-guide-footer">
                <p className="fin-eyebrow">Código de integração</p>
                <code className="fin-guide-code">
                  {buildIframeSnippet(SIMULADOR_URL)}
                </code>
                <button
                  onClick={() =>
                    handleCopy("iframe", buildIframeSnippet(SIMULADOR_URL))
                  }
                  className="fin-btn fin-btn--primary fin-btn--block"
                >
                  {copied === "iframe" ? (
                    <FiCheck size={14} className="fin-btn-check" />
                  ) : (
                    <FiCopy size={14} />
                  )}
                  {copied === "iframe" ? "Copiado!" : "Copiar iframe"}
                </button>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default FinancingSimulatorPage;
